import Vue from "vue"
import axios from "../plugins/axios"
import moment from "moment-timezone"

Vue.prototype.$http = axios
Vue.prototype.$moment = moment

const account = {
    namespaced: true,
    state: {
        account: {},
        user: null,
        token: null,
        displayWelcomeTour: false,
        tags: [],
        contacts: [],
        categories: [],
        communes: [],
        apidaeSelections: [],
        cgu: null,
        cguCadh: null,
        uskaNotifications: 0,
        vtNotificationsCount: 0,
        vtContract: false,
        uskaContract: false,
        hasAdhesionModule: false,
        tourismSectors: [],
        senders: [],
        hasApidaeAuto: false,
    },
    getters: {
        token: (state) => state.token,
        user: (state) => state.user,
    },
    mutations: {
        setUser(state, user) {
            state.user = user
        },
        setAccount(state, data) {
            state.account = data
        },
        setToken(state, token) {
            state.token = token
        },
        setSenders(state, senders) {
            state.senders = senders
        },
        setLogo(state, logo) {
            state.logo = logo
        },
        setCgu(state, cgu) {
            state.cgu = cgu
        },
        setCguCadh(state, cguCadh) {
            state.cguCadh = cguCadh
        },
        setOrganism(state, organism) {
            state.organism = organism
        },
        setDisplayWelcomeTour(state, data) {
            state.displayWelcomeTour = data
        },
        setTags(state, value) {
            state.tags = value
        },
        setCategories(state, value) {
            state.categories = value
        },
        setContacts(state, value) {
            state.contacts = value
        },
        setCommunes(state, value) {
            state.communes = value
        },
        setTourismSectors(state, value) {
          state.tourismSectors = value
        },
        setApidaeSelections(state, value) {
            state.apidaeSelections = value
        },
        setUskaNotifications(state, value) {
            state.uskaNotifications = value
        },
        setVtNotificationsCount(state, value) {
            state.vtNotificationsCount = value
        },
        setVtContract(state) {
            state.vtContract = state.account.current_contracts.filter(contract =>
                contract.contract_offer.code === 'VEILLE_TOURISME').length > 0
        },
        setUskaContract(state) {
            state.uskaContract = state.account.current_contracts.filter(contract =>
                contract.contract_offer.code === 'USKA_FREEMIUM').length > 0
        },
        setHasAdhesionModule(state) {
            state.hasAdhesionModule = state.account.current_contracts.filter(contract => {
                return contract.contract_offer.options.find(option => option.code === "CADH")
            }).length > 0
        },
        setHasApidaeAuto(state) {
            state.hasApidaeAuto = state.account.current_contracts.filter(contract => {
                return contract.contract_offer.options.find(option => option.code === "AUTO_APIDAE_WRITE")
            }).length > 0
        },
    },
    actions: {
        fetchCategories({commit}) {
            Vue.prototype.$http.get("auth/account/categories").then((res) => {
                if (res.body.data !== undefined) {
                    let categories = res.body.data.categories

                    categories.sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }))

                    commit("setCategories", categories)
                }
            })
        },
        fetchCommunes({commit}) {
            Vue.prototype.$http.get("auth/account/communes").then((res) => {
                let communes = res.body.data.communes.map((commune) => {
                    commune.label = commune.name + " (" + commune.code + ")"

                    return commune
                })

                commit("setCommunes", communes)

                let sectors = []
                communes.forEach(commune => {
                    if(commune.tourism_sector !== null && !sectors.includes(commune.tourism_sector)) {
                        sectors.push(commune.tourism_sector)
                    }
                })

                commit('setTourismSectors', sectors)
            })
        },
        loadSenders({commit}, valid) {
            if (valid === undefined) {
                valid = true
            }

            return Vue.prototype.$http.get("auth/account/senders/" + valid).then((res) => {
                commit("setSenders", res.body.data.senders)
            })
        },
        login({commit}, [email, password]) {
            let payload = {
                email: email,
                password: password,
            }

            return Vue.prototype.$http.post("auth/login", payload).then((res) => {
                if (res.body.access_token !== undefined) {
                    commit("setToken", res.body.access_token)
                }
            })
        },
        getCredentials({commit}) {
            return Vue.prototype.$http.get("auth/me").then(
                (res) => {
                    let user = res.body.data.user
                    let account = res.body.data.account

                    commit("setUser", user)
                    commit("setAccount", account)
                    commit("setVtContract")
                    commit("setHasAdhesionModule")
                    commit("setUskaContract")
                    commit('setHasApidaeAuto')

                    let intercomObject = {
                        user_id: user.id,
                        name: user.first_name + " " + user.last_name,
                        email: user.email,
                        role: user.role.name,
                        account_configured: account.configured,
                        platform: "HUBO",
                    }

                    Object.assign(window.intercomSettings, intercomObject)

                    let timezone = account.timezone ?? "Europe/Paris"

                    this._vm.$setUserTimezone(timezone)

                    window.Intercom("update", window.intercomSettings)
                },
                (error) => {
                    // evite de se tapper une boucle infinie si on se deco (comme on requete l'api à chaque changement de route)
                }
            )
        },
        logout({commit}) {
            return Vue.prototype.$http.get("auth/logout").then((res) => {
                commit("setUser", null)
                commit("setToken", null)
            }).catch(error => {
                Vue.prototype.$global.$emit('redirect-login')
                localStorage.clear()
                window.location.href = '/'
            })
        },
        fetchTags({commit}) {
            Vue.prototype.$http.get("auth/account/tags").then((res) => {
                let tags = res.body.data.tags
                tags.sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
                commit("setTags", tags)
            })
        },
        fetchContacts({commit}) {
            Vue.prototype.$http.get("auth/account/contacts").then((res) => {
                commit("setContacts", res.body.data.contacts)
            })
        },
        fetchApidaeSelections({commit}) {
            Vue.prototype.$http.get("auth/account/apidae-selections").then((res) => {
                let selections = res.body.data.selections
                selections.sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }))
                commit("setApidaeSelections", selections)
            })
        },
        getUskaNotificationsCount({commit}) {
            Vue.prototype.$http.get('auth/pro-connexions/notifications').then(res => {
                commit("setUskaNotifications", res.body.data.count)
            })
        },
        getVTNotificationsCount({commit}) {
            Vue.prototype.$http.get('auth/account/waiting-vtc-diffs-count').then(res => {
                commit("setVtNotificationsCount", res.body.data.count)
            })
        }
    },
}

export default account
